import { cn } from "@/lib/cn";
import type { LabelHTMLAttributes, ReactNode } from "react";

export function Label({ className, ...p }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("text-sm font-medium text-fg", className)} {...p} />;
}

export interface FieldProps {
  label?: ReactNode;
  /** Control aligned to the right of the label row — a unit toggle, a "Max"
   *  button. Kept out of the <label> so clicking it doesn't focus the input. */
  action?: ReactNode;
  htmlFor?: string;
  error?: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

// Field — label + control + hint/error, the standard form row. Pass the
// control's id as htmlFor so the label is associated for a11y.
export function Field({ label, action, htmlFor, error, hint, required, className, children }: FieldProps) {
  const labelNode = label ? (
    <Label htmlFor={htmlFor}>
      {label}
      {required && <span className="ml-0.5 text-danger">*</span>}
    </Label>
  ) : null;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {action ? (
        <div className="flex items-center justify-between gap-3">
          {labelNode ?? <span />}
          {action}
        </div>
      ) : (
        labelNode
      )}
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}
